'use client'

import type React from 'react'
import type { PropsWithChildren } from 'react'

import {
  type FontSize,
  getTextBaseClasses,
  type HeadingSize,
  type TextOptions,
} from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'

import { getTestProps, type TestIds } from '../../utils/testing.ts'

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4'

const levelDefaultSizeMap: Record<HeadingLevel, HeadingSize> = {
  h1: '4xl',
  h2: '3xl',
  h3: '2xl',
  h4: 'xl',
}

type HeadingProps = Readonly<
  PropsWithChildren<
    TestIds &
      React.HTMLAttributes<HTMLElement> &
      Omit<TextOptions, 'fontSize'> & {
        fontSize?: HeadingSize
        periodFontSize?: FontSize
        level?: HeadingLevel
      }
  >
>

const Heading = ({
  children,
  className,
  dataTestId,
  dataTrackingId,
  level = 'h1',
  periodFontSize,
  onClick,
  ...textOptions
}: HeadingProps) => {
  const HeadingTag = level as React.ElementType

  return (
    <HeadingTag
      className={cn(
        getTextBaseClasses({
          ...textOptions,
          textColor: textOptions.textColor ?? 'primary',
          fontFamily: textOptions.fontFamily ?? 'heading',
          fontSize: textOptions.fontSize ?? levelDefaultSizeMap[level],
          fontWeight: textOptions.fontWeight ?? 'bold',
        }),
        className,
        onClick ? 'cursor-pointer' : null
      )}
      {...getTestProps({ dataTestId, dataTrackingId })}
      onClick={onClick}
    >
      {children}
    </HeadingTag>
  )
}

export { Heading, type HeadingLevel, type HeadingProps }
